import { useEffect, useState } from 'react';
import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { trimDecimalZeros } from '@/utils/decimal';
import type { ClientPricing, ServicePricing } from '../types';
import type { UpdateFxSpreadPayload, UpdatePricingPayload } from '../useClientDetail';

interface PricingTabProps {
  pricing: ClientPricing | null;
  loading: boolean;
  onLoad: () => Promise<void>;
  onUpdate: (services: UpdatePricingPayload[], fxSpread?: UpdateFxSpreadPayload) => Promise<void>;
}

const DEFAULT_FX_SPREAD = '0.500000';

/** The four editable fee cells of one rail, held as strings while editing. */
interface ServiceDraft {
  outgoing_fee_fixed_value: string;
  outgoing_fee_percent_value: string;
  incoming_fee_fixed_value: string;
  incoming_fee_percent_value: string;
}

const FEE_FIELDS: { key: keyof ServiceDraft; label: string; suffix: string }[] = [
  { key: 'outgoing_fee_fixed_value', label: 'Out. fixed', suffix: '' },
  { key: 'outgoing_fee_percent_value', label: 'Out. %', suffix: '%' },
  { key: 'incoming_fee_fixed_value', label: 'In. fixed', suffix: '' },
  { key: 'incoming_fee_percent_value', label: 'In. %', suffix: '%' },
];

function toDraft(service: ServicePricing): ServiceDraft {
  return {
    outgoing_fee_fixed_value: trimDecimalZeros(service.outgoing_fee_fixed_value),
    outgoing_fee_percent_value: trimDecimalZeros(service.outgoing_fee_percent_value),
    incoming_fee_fixed_value: trimDecimalZeros(service.incoming_fee_fixed_value),
    incoming_fee_percent_value: trimDecimalZeros(service.incoming_fee_percent_value),
  };
}

function isSameDraft(a: ServiceDraft, b: ServiceDraft): boolean {
  return FEE_FIELDS.every(({ key }) => parseFloat(a[key] || '0') === parseFloat(b[key] || '0'));
}

/**
 * A client's tariff, one row per payment rail (#66).
 *
 * A table rather than tabs: adding SEPA or CHIPS adds a row, and an operator
 * comparing rails for a client sees them side by side instead of clicking
 * between them.
 *
 * The FX spread sits in its own block, above and separated. It applies to any
 * conversion whichever rail the funds then take, so showing it as a column
 * would suggest it varies per rail — which it does not.
 */
export function PricingTab({ pricing, loading, onLoad, onUpdate }: PricingTabProps) {
  const [editing, setEditing] = useState(false);
  const [fxSpread, setFxSpread] = useState('');
  const [drafts, setDrafts] = useState<Record<string, ServiceDraft>>({});
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    void onLoad();
  }, [onLoad]);

  const resetFromPricing = () => {
    setFxSpread(trimDecimalZeros(pricing?.fx_spread ?? DEFAULT_FX_SPREAD));
    setDrafts(
      Object.fromEntries(
        (pricing?.services ?? []).map((s) => [s.payment_service_code, toDraft(s)]),
      ),
    );
  };

  useEffect(resetFromPricing, [pricing]);

  const setCell = (code: string, key: keyof ServiceDraft, value: string) => {
    setDrafts((prev) => ({ ...prev, [code]: { ...prev[code], [key]: value } }));
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    const fx = parseFloat(fxSpread);
    if (isNaN(fx) || fx < 0) errs['fx_spread'] = 'Must be a valid non-negative number';
    for (const [code, draft] of Object.entries(drafts)) {
      for (const { key } of FEE_FIELDS) {
        const n = parseFloat(draft[key]);
        if (isNaN(n) || n < 0) errs[`${code}.${key}`] = 'Invalid';
      }
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      // Only the rails the operator actually touched are sent — a screen-level
      // save should not rewrite every tariff and bump every updated_at.
      const changed: UpdatePricingPayload[] = (pricing?.services ?? [])
        .filter((s) => !isSameDraft(drafts[s.payment_service_code], toDraft(s)))
        .map((s) => ({
          payment_service_code: s.payment_service_code,
          ...drafts[s.payment_service_code],
        }));

      const spreadChanged =
        parseFloat(fxSpread || '0') !== parseFloat(pricing?.fx_spread ?? DEFAULT_FX_SPREAD);

      if (changed.length === 0 && !spreadChanged) {
        setEditing(false);
        return;
      }

      await onUpdate(changed, spreadChanged ? { fx_spread: fxSpread } : undefined);
      setEditing(false);
    } catch {
      // Error already toasted
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <LoadingSpinner size="md" text="Loading pricing..." />;
  }

  const services = pricing?.services ?? [];

  return (
    <div className="rounded-xl border border-[#eee] bg-white p-6">
      <div className="mb-5 flex items-center justify-between">
        <div className="text-[16px] font-bold">Pricing Configuration</div>
        {!editing && (
          <Button size="sm" onClick={() => setEditing(true)}>
            Edit Pricing
          </Button>
        )}
      </div>

      {/* FX spread — deliberately outside the per-rail table. */}
      <section
        data-testid="pricing-tab-fx-card"
        className="mb-5 rounded-[10px] border border-[#eee] bg-[#f8f9fa] p-5"
      >
        <div className="mb-2 text-[11px] uppercase tracking-[1px] text-[#999]">FX Spread</div>
        {editing ? (
          <div className="max-w-[220px]">
            <Input
              id="pricing-tab-fx-spread"
              aria-label="FX Spread (%)"
              type="number"
              value={fxSpread}
              onChange={setFxSpread}
              error={errors['fx_spread']}
            />
          </div>
        ) : (
          <div className="text-[18px] font-bold text-[#6B0F1A]">
            {pricing?.fx_spread ?? DEFAULT_FX_SPREAD}%
          </div>
        )}
        <div className="mt-1 text-[11px] text-[#999]">
          Applies to every conversion, whichever service the funds then use.
        </div>
      </section>

      {services.length === 0 ? (
        <div
          data-testid="pricing-tab-empty"
          className="rounded-[10px] border border-dashed border-[#ddd] p-6 text-center text-[13px] text-[#999]"
        >
          No service pricing set for this client — every operation is free.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table data-testid="pricing-tab-table" className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-[#eee] text-left">
                <th className="py-2 pr-4 text-[11px] uppercase tracking-[1px] text-[#999]">
                  Service
                </th>
                {FEE_FIELDS.map(({ key, label }) => (
                  <th
                    key={key}
                    className="py-2 pr-4 text-[11px] uppercase tracking-[1px] text-[#999]"
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {services.map((service) => {
                const code = service.payment_service_code;
                const draft = drafts[code];
                return (
                  <tr
                    key={code}
                    data-testid={`pricing-row-${code}`}
                    className="border-b border-[#f4f4f4]"
                  >
                    <td className="py-3 pr-4 font-semibold text-[#333]">{code}</td>
                    {FEE_FIELDS.map(({ key, label, suffix }) => (
                      <td key={key} className="py-3 pr-4">
                        {editing && draft ? (
                          <div className="max-w-[130px]">
                            <Input
                              id={`pricing-${code}-${key}`}
                              aria-label={`${code} ${label}`}
                              type="number"
                              value={draft[key]}
                              onChange={(v) => setCell(code, key, v)}
                              error={errors[`${code}.${key}`]}
                            />
                          </div>
                        ) : (
                          <span className="text-[#6B0F1A]">
                            {trimDecimalZeros(service[key])}
                            {suffix}
                          </span>
                        )}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="mt-4 flex gap-2">
          <Button onClick={() => void handleSave()} disabled={submitting}>
            {submitting ? 'Saving...' : 'Save Pricing'}
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              resetFromPricing();
              setErrors({});
              setEditing(false);
            }}
          >
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}
